// Betrieb: Telefonie-Verbrauch, Kontaktregeln, Funktionsschalter, Backoffice-Link (Runde 8, Agent 8D): Typen und Client nach
// crm/API.md, Abschnitt 23.4. Verbrauch und Kontaktregeln brauchen `telefonie_verwalten`. 8D erweitert diese Datei nur additiv.
import { http } from "./client";

export interface VerbrauchPostenZeile {
  posten: "anrufe_ausgehend" | "anrufe_eingehend" | "sms" | "nummern" | "sonstiges";
  menge: number | null;
  einheit: "min" | "stueck" | null;
  betrag_eur: number | null;
}

export interface Verbrauch {
  /** JJJJ-MM */
  monat: string;
  posten: VerbrauchPostenZeile[];
  summe_eur: number;
  limit_eur: number | null;
  warnen_ab_prozent: number;
  /** true, wenn die Summe die Warnschwelle erreicht hat */
  warnung: boolean;
  abgerufen_at: string | null;
  /** zusätzlich zum Vertrag */
  waehrung?: string;
  kurs_usd_eur?: number | null;
  limit_erreicht?: boolean;
  prozent?: number | null;
  twilio_eingerichtet?: boolean;
}

export interface VerbrauchVerlaufZeile {
  monat: string;
  summe_eur: number;
  vorhanden?: boolean;
}

export interface Kontaktregeln {
  waehlzeit_von: string;
  waehlzeit_bis: string;
  /** 1 = Montag … 7 = Sonntag */
  tage: number[];
  feiertage_sperren: boolean;
  max_versuche_je_lead_und_tag: number;
  mindest_klingeldauer_s: number;
  wiederwahl_sperre_min: number;
  /** die festen Untergrenzen (Compliance-Linie vom 08.10.2026), die sich nicht lockern lassen */
  grenzen?: KontaktGrenzen;
}

export interface KontaktGrenzen {
  waehlzeit_frueheste: string;
  waehlzeit_spaeteste: string;
  tage_erlaubt: number[];
  feiertage_sperren_fest: boolean;
  max_versuche_hoechstens: number;
  mindest_klingeldauer_mindestens: number;
  wiederwahl_sperre_mindestens: number;
}

export interface Sperrtag {
  datum: string;
  name: string;
  quelle: "baden_wuerttemberg" | "organisation";
}

/** Antwort von GET und PUT /api/betrieb/kontaktregeln (zusätzlich zum Vertrag: wirksam, hinweise, Vorschau) */
export interface KontaktregelnAntwort {
  regeln: Kontaktregeln;
  standard?: Omit<Kontaktregeln, "grenzen">;
  /** was tatsächlich gilt: der strengere Wert aus Maske und Umgebung */
  wirksam?: Omit<Kontaktregeln, "grenzen">;
  hinweise?: string[];
  organisation?: { feiertage_region: string; zusaetzliche_feiertage: string[] };
  naechste_sperrtage?: Sperrtag[];
  jetzt?: { erlaubt: boolean; grund: string | null; meldung: string | null };
}

export interface Funktionsschalter {
  schluessel: string;
  titel: string;
  beschreibung: string | null;
  an: boolean;
  /** frei | nach_freigabe | bewusst_aus */
  rechtsstatus: string;
  /** zusätzlich zum Vertrag (Freigabeprotokoll) */
  schaltbar?: boolean;
  hinweis?: string;
  angebunden?: boolean;
  angebunden_text?: string;
  freigegeben_von?: string | null;
  freigegeben_at?: string | null;
  begruendung?: string | null;
}

export interface SchalterProtokollZeile {
  schluessel: string;
  titel: string;
  an: boolean;
  begruendung: string | null;
  von: string | null;
  at: string | null;
}

export const betriebApi = {
  verbrauch: (monat?: string) => http.get<Verbrauch>("/api/betrieb/verbrauch", { monat }),
  verbrauchVerlauf: (monate = 12) => http.get<{ data: VerbrauchVerlaufZeile[] }>("/api/betrieb/verbrauch/verlauf", { monate }),
  verbrauchAktualisieren: () => http.post<Verbrauch>("/api/betrieb/verbrauch/aktualisieren"),
  limitSetzen: (limit_eur: number | null, warnen_ab_prozent = 80, kurs_usd_eur: number | null = null) =>
    http.put<Verbrauch>("/api/betrieb/verbrauch/limit", { limit_eur, warnen_ab_prozent, kurs_usd_eur }),
  kontaktregeln: () => http.get<KontaktregelnAntwort>("/api/betrieb/kontaktregeln"),
  kontaktregelnSetzen: (e: Partial<Omit<Kontaktregeln, "grenzen">>) => http.put<KontaktregelnAntwort>("/api/betrieb/kontaktregeln", e),
  schalter: () => http.get<{ data: Funktionsschalter[] }>("/api/betrieb/schalter"),
  schalterSetzen: (schluessel: string, an: boolean, begruendung?: string) =>
    http.put<{ schalter: Funktionsschalter }>(`/api/betrieb/schalter/${encodeURIComponent(schluessel)}`, { an, begruendung }),
  schalterProtokoll: () => http.get<{ data: SchalterProtokollZeile[] }>("/api/betrieb/schalter/protokoll"),
  /** Adresse des Backoffice für die Seitenleiste; null: kein Link */
  backofficeLink: () => http.get<{ url: string | null }>("/api/betrieb/backoffice-link"),
};
